<template>
  <div v-if="isAuthenticated" class="app-profile">
    <v-menu v-model="menuOpen" offset-y left nudge-bottom="6" :content-class="compact ? 'app-profile-menu gg-profile-menu' : 'app-profile-menu'" :close-on-content-click="false" transition="app-profile-transition">
      <template v-slot:activator="{ on, attrs }">
        <!-- Compact variant (Globo Graphics header): square avatar + name + caret,
             in a slim bordered chip. Reuses the same dropdown menu below. -->
        <div
          v-if="compact"
          class="app-profile-trigger app-profile-trigger--compact"
          :class="{ 'app-profile-trigger--open': menuOpen }"
          v-bind="attrs"
          v-on="on"
        >
          <span class="app-profile-avatar-square">{{ initials }}</span>
          <span class="app-profile-name-compact">{{ displayName }}</span>
          <v-icon class="app-profile-caret">mdi-menu-down</v-icon>
        </div>
        <div v-else class="app-profile-trigger" :class="{ 'app-profile-trigger--open': menuOpen }" v-bind="attrs" v-on="on">
          <v-avatar size="32" color="primary" class="app-profile-avatar">
            <span class="app-profile-initials">{{ initials }}</span>
          </v-avatar>
          <div class="app-profile-info">
            <span class="app-profile-name">{{ displayName }}</span>
            <span class="app-profile-email">{{ email }}</span>
          </div>
          <v-icon small class="app-profile-chevron">mdi-chevron-down</v-icon>
        </div>
      </template>

      <!-- Globo Graphics dropdown: the module's own two-item menu. Deliberately
           does not carry the SocialCam entries — GG has no logout or Home here. -->
      <v-card v-if="compact" flat class="gg-profile-dropdown">
        <div class="gg-profile-item">
          <span class="gg-profile-item__left">
            <v-icon class="gg-profile-item__icon">mdi-weather-night</v-icon>
            <span class="gg-profile-item__label">Dark Mode</span>
          </span>
          <v-switch
            v-model="isDark"
            hide-details
            dense
            class="gg-profile-switch mt-0 pt-0"
            aria-label="Dark Mode"
            @change="changeTheme"
          />
        </div>
        <button type="button" class="gg-profile-item gg-profile-item--action" @click="onAdvancedSettings">
          <span class="gg-profile-item__left">
            <v-icon class="gg-profile-item__icon">mdi-cog</v-icon>
            <span class="gg-profile-item__label">Configurações avançadas</span>
          </span>
        </button>
      </v-card>

      <v-card v-else flat class="app-profile-dropdown">
        <!-- Menu items -->
        <div class="app-profile-menu-items">
          <router-link v-if="isHome" to="/producoes" class="app-profile-menu-item" @click.native="closeMenu">
            <v-icon small class="mr-3">mdi-cog</v-icon>
            <span>Configurações</span>
          </router-link>
          <router-link v-else to="/" class="app-profile-menu-item" @click.native="closeMenu">
            <v-icon small class="mr-3">mdi-home</v-icon>
            <span>Home</span>
          </router-link>

          <router-link v-if="isAdm && globographicsEnabled" to="/globographics" class="app-profile-menu-item" @click.native="closeMenu">
            <v-icon small class="mr-3">mdi-palette-outline</v-icon>
            <span>Globo Graphics</span>
          </router-link>
        </div>

        <v-divider class="app-profile-divider" />

        <!-- Theme & settings -->
        <div class="app-profile-settings">
          <div class="app-profile-setting">
            <div class="app-profile-setting-left">
              <v-icon small class="mr-3" color="text_secondary">mdi-theme-light-dark</v-icon>
              <span>{{ isDark ? 'Modo Escuro' : 'Modo Claro' }}</span>
            </div>
            <v-switch
              v-model="isDark"
              @change="changeTheme"
              color="primary"
              hide-details
              dense
              class="mt-0 pt-0"
            />
          </div>
          <div v-if="showAutoUpdate" class="app-profile-setting">
            <div class="app-profile-setting-left">
              <v-icon small class="mr-3" color="text_secondary">mdi-refresh-auto</v-icon>
              <span>Atualizar auto</span>
            </div>
            <v-switch
              v-model="localUpdateMode"
              @change="changeUpdateMode"
              color="primary"
              hide-details
              dense
              class="mt-0 pt-0"
            />
          </div>
        </div>

        <v-divider class="app-profile-divider" />

        <!-- Feedback -->
        <div class="app-profile-menu-items">
          <div class="app-profile-menu-item app-profile-feature" @click="showComingSoon">
            <v-icon small class="mr-3">mdi-lightbulb-outline</v-icon>
            <span>Solicitar Funcionalidade</span>
          </div>
        </div>

        <v-divider class="app-profile-divider" />

        <!-- Logout -->
        <div class="app-profile-menu-items">
          <div class="app-profile-menu-item app-profile-logout" @click="signOut()">
            <v-icon small class="mr-3" color="error">mdi-logout</v-icon>
            <span>Sair</span>
          </div>
        </div>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

if (!Vue.options.components["app-profile-transition"]) {
  Vue.component("app-profile-transition", {
    functional: true,
    render(h, ctx) {
      return h("transition", { props: { name: "app-profile-transition" } }, ctx.children);
    },
  });
}

export default {
  name: "AppProfile",
  props: {
    // Slim bordered chip used by the Globo Graphics header (square avatar,
    // name only, no email). Default false keeps the AppBar pill unchanged.
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      menuOpen: false,
      isDark: this.$vuetify.theme.dark,
      localUpdateMode: false,
    };
  },
  mounted() {
    const stored = JSON.parse(localStorage.getItem("@socialcam/modoAtualizacaoColabs"));
    this.localUpdateMode = stored === 1;
  },
  computed: {
    ...mapGetters({
      username: "users/username",
      displayName: "users/displayName",
      email: "users/email",
      isAdm: "users/isAdm",
      isAuthenticated: "users/isAuthenticated",
    }),
    isHome() {
      return this.$route.path === "/";
    },
    initials() {
      const parts = (this.displayName || "").trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return (this.displayName || this.username || "").substring(0, 2).toUpperCase();
    },
    // "Globo Graphics" (the Vizrt module) is per-production opt-in.
    globographicsEnabled() {
      return this.$store.state.producoes.selectedProducao?.settings?.globographics?.enabled || false;
    },
    showAutoUpdate() {
      return this.$store.state.moderacao.modoModeracao === 0;
    },
  },
  methods: {
    ...mapActions({
      logout: "users/logout",
      setColabsUpdateMode: "producoes/setColabsUpdateMode",
    }),
    closeMenu() {
      setTimeout(() => {
        this.menuOpen = false;
      }, 150);
    },
    async signOut() {
      this.closeMenu();
      await this.logout();
    },
    changeTheme(e) {
      localStorage.setItem("@socialcam/is_dark_theme", e);
      this.$vuetify.theme.dark = e;
    },
    changeUpdateMode(mode) {
      this.setColabsUpdateMode(mode ? 1 : 0);
      localStorage.setItem("@socialcam/modoAtualizacaoColabs", mode ? 1 : 0);
    },
    showComingSoon() {
      this.closeMenu();
      this.$toast.info("Solicitar funcionalidade — em breve!");
    },
    // The settings dialog is production-scoped, so the host view owns it.
    onAdvancedSettings() {
      this.closeMenu();
      this.$emit("advanced-settings");
    },
  },
};
</script>

<style lang="scss">
.app-profile-menu {
  border-radius: var(--sc-radius-lg) !important;
  margin-top: 4px;
  border: 1px solid var(--sc-border-subtle);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35) !important;
  transform-origin: top right !important;
  will-change: transform, opacity;
}

/* Globo Graphics menu — teleported to the app root, so it needs the module
   font and palette here rather than in the scoped block. */
.gg-profile-menu {
  font-family: "Inter", "Globotipo", sans-serif;
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md) !important;
  box-shadow: var(--sc-gg-shadow-low) !important;

  .gg-profile-dropdown {
    min-width: 256px;
    padding: var(--sc-space-1);
    background: var(--sc-gg-surface-1) !important;
    border-radius: var(--sc-radius-md) !important;
  }

  .gg-profile-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sc-space-1);
    width: 100%;
    min-height: 32px;
    padding: 6px;
    background: transparent;
    border: none;
    border-radius: var(--sc-radius-sm);
    text-align: left;
    transition: background var(--sc-duration-fast) var(--sc-easing-standard);
  }

  .gg-profile-item--action {
    cursor: pointer;

    &:hover {
      background: var(--sc-surface-tint-hover);
    }
  }

  .gg-profile-item__left {
    display: flex;
    flex: 1 0 0;
    align-items: center;
    gap: var(--sc-space-2);
    min-width: 0;
    padding-right: var(--sc-space-1);
  }

  .gg-profile-item__icon {
    flex-shrink: 0;
    color: var(--sc-gg-text-strong) !important;
    font-size: 16px !important;
  }

  .gg-profile-item__label {
    overflow: hidden;
    // The global `span { color }` rule would otherwise grey these labels.
    color: var(--sc-gg-text-strong);
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.07px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* Switch — accent track when on, per the DS control. */
  .gg-profile-switch {
    flex-shrink: 0;
    margin: 0;
    padding: 0;

    .v-input--selection-controls__input {
      margin-right: 0;
    }

    .v-input--switch__track {
      opacity: 1;
      color: var(--sc-gg-border-200) !important;
    }

    .v-input--switch__thumb {
      color: var(--sc-gg-text-primary) !important;
    }

    &.v-input--is-label-active .v-input--switch__track {
      color: var(--sc-gg-accent) !important;
    }

    .v-input--selection-controls__ripple {
      display: none;
    }
  }
}

.app-profile-transition-enter-active {
  transition: opacity 180ms cubic-bezier(0.2, 0, 0, 1),
              transform 220ms cubic-bezier(0.2, 0.9, 0.2, 1.1);
}

.app-profile-transition-leave-active {
  transition: opacity 120ms cubic-bezier(0.4, 0, 1, 1),
              transform 140ms cubic-bezier(0.4, 0, 1, 1);
}

.app-profile-transition-enter,
.app-profile-transition-leave-to {
  opacity: 0;
  transform: scale(0.85) translateY(-4px);
}

.app-profile-transition-enter-to,
.app-profile-transition-leave {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>

<style lang="scss" scoped>
.app-profile-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: var(--sc-radius-pill);
  background: var(--sc-surface-tint);
  border: 1px solid var(--sc-border-subtle);
  transition: background var(--sc-duration-base) var(--sc-easing-standard),
              border-color var(--sc-duration-base) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint-hover);
    border-color: var(--sc-border-medium);
  }
}

.app-profile-avatar {
  flex-shrink: 0;
}

.app-profile-initials {
  color: #0a0a0a; // V2 on_primary — fixes WCAG contrast on green avatar bg
  font-weight: var(--sc-weight-bold);
  font-size: var(--sc-text-lg);
}

.app-profile-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.app-profile-name {
  font-size: var(--sc-text-base);
  font-weight: 600;
  color: var(--v-text_primary-base);
  line-height: 1.2;
}

.app-profile-email {
  font-size: 0.7rem;
  color: var(--v-text_secondary-base);
  line-height: 1.2;
}

.app-profile-chevron {
  color: var(--v-text_secondary-base) !important;
  opacity: 0.6;
  margin-left: 2px;
  transition: transform var(--sc-duration-base) var(--sc-easing-standard);
}

.app-profile-trigger--open .app-profile-chevron {
  transform: rotate(180deg);
}

/* Compact variant — Globo Graphics header chip */
.app-profile-trigger--compact {
  gap: var(--sc-space-2);
  height: 32px;
  padding: var(--sc-space-1);
  border-radius: var(--sc-radius-sm);
  border: 1px solid var(--sc-gg-border-200);
  background: transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: var(--sc-gg-border-200);
  }
}

.app-profile-avatar-square {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: var(--sc-radius-sm);
  border: 0.25px solid var(--sc-gg-border-100);
  background: var(--sc-gg-surface-1);
  color: var(--sc-gg-text-tertiary);
  font-size: 12px;
  font-weight: var(--sc-weight-regular);
  line-height: 16px;
}

.app-profile-name-compact {
  color: var(--sc-gg-text-secondary);
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.06px;
  white-space: nowrap;
}

.app-profile-caret {
  color: var(--sc-gg-text-strong) !important;
  font-size: 16px !important;
  transition: transform var(--sc-duration-base) var(--sc-easing-standard);
}

.app-profile-trigger--compact.app-profile-trigger--open .app-profile-caret {
  transform: rotate(180deg);
}

/* Dropdown */
.app-profile-dropdown {
  background: var(--v-surface_secondary-base) !important;
  border-radius: var(--sc-radius-lg) !important;
  overflow: hidden;
  min-width: 260px;
}

.app-profile-divider {
  border-color: var(--sc-border-subtle) !important;
  margin: 0;
}

/* Menu items */
.app-profile-menu-items {
  padding: 6px;
}

.app-profile-menu-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: var(--sc-radius-md);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);
  text-decoration: none;
  color: var(--v-text_primary-base);
  font-size: var(--sc-text-base);
  font-weight: 500;

  &:hover {
    background: var(--sc-surface-tint-hover);
  }
}

.app-profile-feature:hover {
  background: rgba(255, 193, 7, 0.08);
  .v-icon {
    color: var(--sc-color-feature) !important;
  }
}

.app-profile-logout {
  color: var(--v-error-base);
  &:hover {
    background: rgba(var(--sc-color-error-rgb), 0.08);
  }
}

/* Settings */
.app-profile-settings {
  padding: 6px;
}

.app-profile-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: var(--sc-radius-md);
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: var(--sc-surface-tint);
  }
}

.app-profile-setting-left {
  display: flex;
  align-items: center;
  font-size: var(--sc-text-base);
  color: var(--v-text_primary-base);
}

/* Switch overrides */
::v-deep .v-input--switch { margin: 0; padding: 0; }
</style>
